import { customElement, property } from 'lit/decorators.js';

import { ChartBase } from './base';

@customElement('echarts-table')
export class TableChart extends ChartBase {
  @property({ type: Boolean, attribute: 'show-legend', reflect: true })
  showLegend: boolean = false;

  @property({ type: String, attribute: 'colors' })
  colors: string = '';

  @property({ type: Object, attribute: true, reflect: true })
  override data: object[] = [];

  @property({ type: Object, attribute: 'titles', reflect: true })
  titles: { key: string; name: string; width: number }[] = [];

  override disconnectedCallback() {
    super.disconnectedCallback();
  }

  override firstUpdated(changedProperties: Map<PropertyKey, unknown>) {
    super.firstUpdated(changedProperties);
  }

  override attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null,
  ): void {
    super.attributeChangedCallback(name, _old, value);
    this.requestUpdate();
  }

  override get options() {
    if (this.loading) {
      return this.loadingOptions;
    }
    const _titles = this.titles;
    const data = this.mapData(this.data);
    const width = this.chart?.getWidth() ?? 400;
    const height = this.chart?.getHeight() ?? 120;
    const titles = this.createTitle(_titles);
    const keys = this.titles.map((n) => n.key);
    const viewLen = (height - 60) / 30;
    const showZoom = viewLen < data.length;
    const max = showZoom ? data.length : viewLen;

    return {
      animation: false,
      graphic: [
        {
          type: 'group',
          id: 'title',
          left: 30,
          top: 30,
          children: titles,
        },
      ],
      tooltip: {
        formatter(params) {
          return `${params.data.join(' ')}`;
        },
      },
      grid: {
        show: false,
        left: 30,
        top: 30,
        right: 30,
        bottom: 30,
      },
      dataZoom: {
        show: showZoom,
        type: 'slider',
        zoomLock: true,
        orient: 'vertical',
        right: '5%',
        backgroundColor: 'transparent',
        showDetail: false,
        showDataShadow: false,
        start: 0,
        end: ((height - 90) / (30 * data.length)) * 100,
      },
      xAxis: {
        show: false,
      },
      yAxis: {
        show: false,
        scale: true,
        inverse: true,
        boundaryGap: false,
        min: 0,
        max: showZoom ? max + 1 : max,
      },
      series: [
        {
          type: 'custom',
          coordinateSystem: 'cartesian2d',
          barWidth: 30,
          barCategoryGap: 0,
          data,
          encode: {
            x: keys.indexOf('value'),
            y: keys.length,
          },
          renderItem(
            params: { coordSys: { x: number; y: number }; dataIndex: number },
            api,
          ) {
            const ikeys = keys;
            const i = params.dataIndex;
            const { coordSys } = params;
            const apiCoord = api.coord([api.value(3), api.value(ikeys.length)]);
            const top = apiCoord[1] as number;
            let offset = 0;

            const createColumn = (
              title: { key: string; name: string; width: number },
              ii: number,
            ) => {
              const offsetLeft = offset * (width * 0.9);
              offset += title.width;
              const text = api.value(ii);

              return {
                type: 'group',
                z: 100,
                children: [
                  {
                    type: 'rect',
                    z: 100,
                    shape: {
                      x: offsetLeft + coordSys.x,
                      y: top,
                      width: width * 0.9 * title.width,
                      height: 30,
                    },
                    style: {
                      fill: i % 2 === 0 ? '#eeeeee' : '#dddddd',
                      stroke: 'none',
                      strokeWidth: 0,
                      lineWidth: 1,
                      shadowBlur: 0,
                      shadowOffsetX: 0,
                      shadowOffsetY: 0,
                      shadowColor: 'rgba(0,0,0,0.3)',
                    },
                  },
                  {
                    type: 'text',
                    style: {
                      x: offsetLeft + coordSys.x + 5,
                      y: top + 15,
                      text,
                      textVerticalAlign: 'middle',
                      font: '12px arial',
                    },
                  },
                ],
              };
            };

            const columns = _titles.map(createColumn);

            return {
              type: 'group',
              z: 100,
              style: api.style(),
              children: columns,
            };
          },
        },
      ],
    };
  }

  setData(data: object[]) {
    this.data = data;
    this.requestUpdate();
  }

  mapData(data: object[]) {
    const keys = this.titles.map((n) => n.key);
    return data.map((d, idx) =>
      keys
        .reduce<unknown[]>((l, k) => {
          const v = d[k];
          l.push(v);
          return l;
        }, [])
        .concat(idx + 1),
    );
  }

  createTitle(titles) {
    const width = this.chart?.getWidth() ?? 400;
    const height = 30;
    let offset = 0;

    return titles.map((title, _i) => {
      const { key, name } = title;
      const offsetLeft = offset * (width * 0.9);
      offset += title.width;

      return {
        type: 'group',
        id: key,
        top: 0,
        left: offsetLeft,
        height: 30,
        children: [
          {
            type: 'rect',
            z: 100,
            left: 0,
            top: 0,
            shape: {
              width: width * 0.9 * title.width,
              height,
            },
            style: {
              fill: '#ddd',
              stroke: 'none',
              strokeWidth: 0,
              lineWidth: 1,
              shadowBlur: 0,
              shadowOffsetX: 0,
              shadowOffsetY: 0,
              shadowColor: 'rgba(0,0,0,0.3)',
            },
          },
          {
            type: 'text',
            z: 100,
            top: 'middle',
            left: 5,
            style: {
              text: name,
              textVerticalAlign: 'middle',
              font: '12px arial',
            },
          },
        ],
      };
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'echarts-table': TableChart;
  }
}
